import { format, addDays, addWeeks, parseISO, isAfter, isBefore, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import type { RecurrenceRule } from '@/types';

const WEEKDAY_LABELS: Record<number, string> = {
  0: 'Dom',
  1: 'Lun',
  2: 'Mar',
  3: 'Mié',
  4: 'Jue',
  5: 'Vie',
  6: 'Sáb',
};

const DEFAULT_INTERVAL = 1;
const DEFAULT_UNIT = 'WEEK' as const;

/**
 * Returns the weekday (0 = Sun … 6 = Sat) for a datetime-local or ISO string.
 */
export function getWeekdayFromStartsAt(startsAt: string): number {
  return startsAt ? new Date(startsAt).getDay() : 1;
}

/**
 * Ensures at least one weekday is selected; falls back to the start date's day.
 */
export function ensureByWeekDay(
  byWeekDay: number[] | undefined,
  startsAt: string,
): number[] {
  if (byWeekDay && byWeekDay.length > 0) return byWeekDay;
  return [getWeekdayFromStartsAt(startsAt)];
}

/**
 * Default recurrence rule for a new recurring event anchored on `startsAt`.
 */
export function buildDefaultRecurrenceRule(startsAt: string): RecurrenceRule {
  return {
    interval: DEFAULT_INTERVAL,
    unit: DEFAULT_UNIT,
    byWeekDay: [getWeekdayFromStartsAt(startsAt)],
    until: '',
  };
}

/**
 * Field-level validation errors for a recurrence rule.
 */
export interface RecurrenceRuleFieldErrors {
  until?: string;
  byWeekDay?: string;
}

/**
 * Validates a recurrence rule before submit. Returns per-field error messages.
 */
export function validateRecurrenceRule(
  rule: RecurrenceRule,
): RecurrenceRuleFieldErrors {
  const errors: RecurrenceRuleFieldErrors = {};

  if (!rule.until) {
    errors.until = 'Indicá la fecha de finalización de la serie.';
  }

  if (rule.unit === 'WEEK' && !rule.byWeekDay?.length) {
    errors.byWeekDay = 'Elegí al menos un día de la semana para la recurrencia.';
  }

  return errors;
}

/**
 * Returns a list of ISO datetime strings for every occurrence of a recurring
 * event between `startsAt` (inclusive) and `rule.until` (inclusive, date only).
 *
 * @param startsAt - ISO datetime of the first occurrence
 * @param rule     - Recurrence rule
 * @returns Array of ISO datetime strings for each occurrence start
 */
export function generateOccurrences(startsAt: string, rule: RecurrenceRule): string[] {
  const occurrences: string[] = [];
  const untilDate = parseISO(rule.until + 'T23:59:59');
  let current = parseISO(startsAt);

  if (isAfter(current, untilDate)) return occurrences;

  const byWeekDay = new Set(rule.byWeekDay ?? []);

  while (!isAfter(current, untilDate)) {
    if (rule.unit === 'DAY') {
      occurrences.push(current.toISOString());
      current = addDays(current, rule.interval);
    } else {
      // WEEK: iterate over each configured weekday in the current week cycle,
      // starting from the anchor day, then advance by `interval` weeks.
      const anchor = current;
      const weekdaysToVisit = byWeekDay.size > 0 ? Array.from(byWeekDay).sort() : [getDay(anchor)];

      for (const day of weekdaysToVisit) {
        const diff = day - getDay(anchor);
        const candidate = addDays(anchor, diff < 0 ? diff + 7 : diff);
        if (!isBefore(candidate, parseISO(startsAt)) && !isAfter(candidate, untilDate)) {
          occurrences.push(candidate.toISOString());
        }
      }

      current = addWeeks(anchor, rule.interval);
    }
  }

  return occurrences;
}

/**
 * Produces a human-readable summary of a recurrence rule in Spanish.
 * Example: "Lunes y miércoles, cada semana, 19:00–21:00, hasta el 30 sept 2026"
 *
 * @param rule     - Recurrence rule
 * @param startsAt - ISO datetime of the first occurrence (for time display)
 * @param endsAt   - ISO datetime of the first occurrence end (for time display)
 */
export function formatRecurrenceSummary(
  rule: RecurrenceRule,
  startsAt: string,
  endsAt: string,
): string {
  if (!rule.until || !startsAt || !endsAt) return '';

  const startTime = format(parseISO(startsAt), 'HH:mm');
  const endTime = format(parseISO(endsAt), 'HH:mm');
  const untilFormatted = format(parseISO(rule.until), "d 'de' MMM yyyy", { locale: es });

  const intervalPart =
    rule.interval === 1
      ? rule.unit === 'DAY'
        ? 'Todos los días'
        : 'cada semana'
      : rule.unit === 'DAY'
        ? `cada ${rule.interval} días`
        : `cada ${rule.interval} semanas`;

  let daysPart = '';
  if (rule.unit === 'WEEK' && rule.byWeekDay && rule.byWeekDay.length > 0) {
    const labels = rule.byWeekDay.map((d) => WEEKDAY_LABELS[d]).filter(Boolean);
    if (labels.length === 1) {
      daysPart = labels[0];
    } else {
      const last = labels.pop()!;
      daysPart = `${labels.join(', ')} y ${last}`;
    }
  }

  const parts = [
    daysPart || null,
    intervalPart,
    `${startTime}–${endTime}`,
    `hasta el ${untilFormatted}`,
  ].filter(Boolean);
  return parts.join(', ');
}

/**
 * Returns the abbreviated label (D, L, M, X, J, V, S) for each weekday used in the pills.
 */
export const WEEKDAY_PILL_OPTIONS: Record<string, string> = {
  '0': 'D',
  '1': 'L',
  '2': 'M',
  '3': 'X',
  '4': 'J',
  '5': 'V',
  '6': 'S',
};
